import {
  AfterContentChecked,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { MatFormFieldAppearance, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { takeWhile } from 'rxjs';
import { FormControlAttributeDirective } from '../../directives';
import { CustomFormControl } from '../../forms/base';
import { customFormControlProviders } from '../../forms/providers/custom-form-control-providers';

@Component({
  selector: 'smata-single-input',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    CommonModule,
    FormControlAttributeDirective,
  ],
  templateUrl: './single-input.component.html',
  styleUrls: ['./single-input.component.scss', '../../style/common/common.scss'],
  providers: [customFormControlProviders(SmataSingleInputComponent)],
})
export class SmataSingleInputComponent<T>
  extends CustomFormControl<T>
  implements OnInit, AfterViewInit, AfterContentChecked, OnDestroy
{
  @Input() appearance: MatFormFieldAppearance = 'fill';
  @Input() label?: string;
  @Input() inputClass = '';
  @Input() prefix = '';
  @Input() suffix = '';
  @Input() maxlength = 100;

  @Input() hintMaxLength = 0;
  @Input() type: 'number' | 'text' = 'text';
  @Input() icon?: string;
  @Input() placeholder = '';
  @Input() showNumberArrows = true;

  @Output() formControlFocus = new EventEmitter();
  @Output() formControlBlur = new EventEmitter<FocusEvent>();

  @ViewChild('errorArea') errorArea?: ElementRef;

  componentFocused = false;
  public textLength = 0;
  readonly formControl = new FormControl<T | undefined>(undefined);

  get prefixCondition() {
    return (
      this.prefix &&
      ((this.formControl.value !== null &&
        this.formControl.value !== undefined &&
        this.formControl.value !== '') ||
        (this.componentFocused && this.prefix))
    );
  }

  private componentActive = true;

  constructor(private readonly el: ElementRef) {
    super(el);
  }

  protected onWriteValue(value: T | undefined): void {
    this.formControl.setValue(value);
  }

  protected onDisable(disabled: boolean): void {
    if (disabled) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }
  }

  protected onValidate(_: AbstractControl<any, any>): ValidationErrors | null {
    return null;
  }

  @HostListener('blur')
  onBlur() {
    this.emitFormControlTouched();
  }

  onFocus() {
    this.formControlFocus.emit();
    this.componentFocused = true;
  }

  @HostListener('focusout')
  onFocusOut() {
    this.emitFormControlTouched();
    this.componentFocused = false;
  }

  onKeyDown(e: KeyboardEvent) {
    if (this.type === 'number' && !this.showNumberArrows) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
      }
    }
  }

  handleBlur(event: FocusEvent) {
    this.formControlBlur.emit(event);
  }

  ngOnInit(): void {
    this.formControl.valueChanges.pipe(takeWhile(() => this.componentActive)).subscribe((text) => {
      const value = this.type === 'number' ? Number(text) : text;
      this.emitFormControlChange((value as T) ?? undefined);

      if (this.hintMaxLength && typeof text === 'string') {
        this.textLength = text.length;
      }
    });

    this.validationErrorOnTouched$
      .pipe(takeWhile(() => this.componentActive))
      .subscribe((error) => this.setErrorToInternalFormControl(error));
  }

  ngAfterViewInit(): void {
    this.listenToStatusChange();
  }

  ngAfterContentChecked(): void {
    const errorTags = this.errorArea?.nativeElement.querySelector('mat-error');
    const error = errorTags ? { error: true } : null;
    this.setErrorToInternalFormControl(error);
  }

  ngOnDestroy(): void {
    this.componentActive = false;
  }

  getInputClass(): string {
    let inputClass = this.inputClass;
    if (this.appearance == 'outline') {
      inputClass += ` smata-client-ng-text-input-outline`;
    }
    if (this.prefixCondition) {
      inputClass += ' has-value-prefix';
    }
    return inputClass;
  }

  private setErrorToInternalFormControl(error: ValidationErrors | null) {
    this.formControl.setErrors(error);
    this.formControl.markAsTouched();
  }
}
